import {
  MouseCalibrationFeature,
  CalibrationSaveType,
  CalibrationStatus,
  TriggerStatus,
  RtSide,
  RtVibrationFeedbackType,
} from '../../../../features/mouse-calibration/contract'
import { checkACK, sendRead, sendWrite } from '../../../lm20-rt/capabilities/memory'
import { getAddress } from '../../../../utils/common'
import type { LevelAddress } from '../../../../types/protocol'
import { CMD_LM20_RT, PROTOCOL_CONFIG_LM20_RT } from '../../protocols/cmd'
import { ADDRESS_LM20_RT } from '../../protocols/address'

const REPORT_ID = PROTOCOL_CONFIG_LM20_RT.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG_LM20_RT.feature_report_id

type RtField =
  | 'switch'
  | 'triggerPress'
  | 'rtLevel'
  | 'vibrationPress'
  | 'vibrationRelease'
  | 'beepPress'
  | 'beepRelease'
  | 'beepVolume'

/**
 * RT 磁轴校准命令常量（《VT系列LM20 RT磁轴鼠标端USB通信协议 v1.07》命令码 0xB0~0xB5）
 * 命令码统一从 CMD_LM20_RT 引用，避免重复定义；每个 SUB_CMD 后携带的固定首参
 * （0x4f/0x4e/0x4d/0x4c/0x4b/0x4a）为协议规定的固定字参数。
 * SET_REPORT 数据包结构：[0x06, 0xFF, SUB_CMD, ...数据]，
 * 此处 payload 经 sendAndWait 透传为 [SUB_CMD, ...数据]（与现有 pairing 实现一致）。
 */

/**
 * nrf54L RT 磁轴鼠标校准能力实现。
 * 覆盖协议命令 0xB0~0xB5 及磁轴 EEPROM 字段级读写。
 */
export class LM20RTMouseCalibration extends MouseCalibrationFeature {
  /** 解析当前 configIndex：未传入时回退到 device.currentConfig.index，再回退到 0 */
  private resolveConfigIndex(configIndex?: number): number {
    if (typeof configIndex === 'number') return configIndex
    return this.device.currentConfig?.index ?? 0
  }

  /** 发送一条校准命令并等待 ACK（非忙碌）。
   *  payload 格式与正常命令一致：[connectionType, subCmd, ...args]
   *  （协议 SET_REPORT 数据包 [0x06, 0xFF, SUB_CMD, ...]，其中 0xFF 为有线 connectionType）
   */
  private async sendCalibrationCmd(subCmd: number, ...args: number[]): Promise<DataView> {
    const res = await this.device.sendAndWait(
      REPORT_ID,
      new Uint8Array([this.device.connectionType, subCmd, ...args]),
      FEATURE_REPORT_ID,
      (data) => data.getUint8(0) !== 0x02,
      { maxRetries: 5 },
    )
    checkACK(res)
    return res
  }

  /** 进入校准模式 (0xB0) */
  async enterCalibration(): Promise<void> {
    await this.sendCalibrationCmd(CMD_LM20_RT.enter_rt_cal[0], 0x4f)
  }

  /** 不保存退出校准模式 (0xB1) */
  async exitCalibrationWithoutSave(): Promise<void> {
    await this.sendCalibrationCmd(CMD_LM20_RT.exit_rt_cal[0], 0x4e)
  }

  /** 保存并退出校准模式 (0xB2)，saveType: 0=厂家校准, 1=用户校准 */
  async exitCalibrationAndSave(saveType: CalibrationSaveType = 0): Promise<void> {
    await this.sendCalibrationCmd(CMD_LM20_RT.exit_and_save_rt_cal[0], 0x4d, saveType)
  }

  /** 重新开始校准 (0xB3) */
  async restartCalibration(): Promise<void> {
    await this.sendCalibrationCmd(CMD_LM20_RT.restart_rt_cal[0], 0x4c)
  }

  /** 读取校准状态 (0xB4) */
  async readCalibrationStatus(): Promise<CalibrationStatus> {
    const res = await this.sendCalibrationCmd(CMD_LM20_RT.get_rt_cal_status[0], 0x4b)
    const readChannel = (base: number) => ({
      adc: res.getUint16(base, true),
      lta: res.getUint16(base + 2, true),
      delta: res.getInt16(base + 4, true),
      ndelta: res.getInt16(base + 6, true),
      maxDelta: res.getUint16(base + 8, true),
      level: res.getUint8(base + 10),
    })
    const flagByte = res.getUint8(1)
    return {
      flag: {
        inCalibrationMode: ((flagByte >> 0) & 1) === 1,
        leftPassed: ((flagByte >> 1) & 1) === 1,
        rightPassed: ((flagByte >> 2) & 1) === 1,
        leftTriggered: ((flagByte >> 3) & 1) === 1,
        rightTriggered: ((flagByte >> 4) & 1) === 1,
      },
      left: readChannel(2),
      right: readChannel(13),
    }
  }

  /** 读取当前行程状态 (0xB5) */
  async readTriggerStatus(): Promise<TriggerStatus> {
    const res = await this.sendCalibrationCmd(CMD_LM20_RT.get_rt_cal_active_status[0], 0x4a)
    return {
      leftTriggerLevel: res.getUint8(1),
      leftTriggerStatus: res.getUint8(2),
      rightTriggerLevel: res.getUint8(3),
      rightTriggerStatus: res.getUint8(4),
    }
  }

  /** 按侧与字段查找 EEPROM 生成地址（未叠加 configIndex） */
  private rtAddress(side: RtSide, field: RtField): LevelAddress {
    const map = {
      switch: { left: ADDRESS_LM20_RT.RT_LEFT_SWITCH, right: ADDRESS_LM20_RT.RT_RIGHT_SWITCH },
      triggerPress: {
        left: ADDRESS_LM20_RT.RT_LEFT_TRAVEL,
        right: ADDRESS_LM20_RT.RT_RIGHT_TRAVEL,
      },
      rtLevel: {
        left: ADDRESS_LM20_RT.RT_LEFT_RT_TRAVEL,
        right: ADDRESS_LM20_RT.RT_RIGHT_RT_TRAVEL,
      },
      vibrationPress: {
        left: ADDRESS_LM20_RT.RT_LEFT_VIBRATION_PRESS,
        right: ADDRESS_LM20_RT.RT_RIGHT_VIBRATION_PRESS,
      },
      vibrationRelease: {
        left: ADDRESS_LM20_RT.RT_LEFT_VIBRATION_RELEASE,
        right: ADDRESS_LM20_RT.RT_RIGHT_VIBRATION_RELEASE,
      },
      beepPress: {
        left: ADDRESS_LM20_RT.RT_LEFT_SOUND_PRESS,
        right: ADDRESS_LM20_RT.RT_RIGHT_SOUND_PRESS,
      },
      beepRelease: {
        left: ADDRESS_LM20_RT.RT_LEFT_SOUND_RELEASE,
        right: ADDRESS_LM20_RT.RT_RIGHT_SOUND_RELEASE,
      },
      beepVolume: {
        left: ADDRESS_LM20_RT.RT_LEFT_SOUND_VOLUME,
        right: ADDRESS_LM20_RT.RT_RIGHT_SOUND_VOLUME,
      },
    } as const
    return map[field][side]
  }

  private async readRtField(side: RtSide, field: RtField, configIndex?: number): Promise<number> {
    const idx = this.resolveConfigIndex(configIndex)
    const addr = getAddress(this.rtAddress(side, field), idx)
    return sendRead(this.device, addr, 1, (data) => data.getUint8(4))
  }

  private async writeRtField(
    side: RtSide,
    field: RtField,
    value: number,
    configIndex?: number,
  ): Promise<void> {
    const idx = this.resolveConfigIndex(configIndex)
    const addr = getAddress(this.rtAddress(side, field), idx)
    await sendWrite(this.device, addr, [value])
  }

  // ===== RT 开关 (1 byte) =====

  async getRtSwitch(side: RtSide, configIndex?: number): Promise<boolean> {
    return (await this.readRtField(side, 'switch', configIndex)) !== 0
  }

  async setRtSwitch(side: RtSide, value: boolean, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'switch', value ? 1 : 0, configIndex)
  }

  // ===== 触发级别（press / rt 各自直接读写 1 byte） =====

  async getTriggerPress(side: RtSide, configIndex?: number): Promise<number> {
    return this.readRtField(side, 'triggerPress', configIndex)
  }

  async setTriggerPress(side: RtSide, value: number, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'triggerPress', value, configIndex)
  }

  async getRtLevel(side: RtSide, configIndex?: number): Promise<number> {
    return this.readRtField(side, 'rtLevel', configIndex)
  }

  async setRtLevel(side: RtSide, value: number, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'rtLevel', value, configIndex)
  }

  // ===== 振动（press / release 各自直接读写 1 byte） =====

  async getVibrationPress(side: RtSide, configIndex?: number): Promise<number> {
    return this.readRtField(side, 'vibrationPress', configIndex)
  }

  async setVibrationPress(side: RtSide, value: number, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'vibrationPress', value, configIndex)
  }

  async getVibrationRelease(side: RtSide, configIndex?: number): Promise<number> {
    return this.readRtField(side, 'vibrationRelease', configIndex)
  }

  async setVibrationRelease(side: RtSide, value: number, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'vibrationRelease', value, configIndex)
  }

  // ===== 振动反馈类型（左右键共享 1 byte） =====

  async getVibrationFeedbackType(configIndex?: number): Promise<RtVibrationFeedbackType> {
    const idx = this.resolveConfigIndex(configIndex)
    const addr = getAddress(ADDRESS_LM20_RT.RT_VIBRATION_FEEDBACK_TYPE, idx)
    const value = await sendRead(this.device, addr, 1, (data) => data.getUint8(4))
    if (value !== 0 && value !== 1 && value !== 2) {
      throw new RangeError(`Invalid vibration feedback type: ${value}`)
    }
    return value
  }

  async setVibrationFeedbackType(
    value: RtVibrationFeedbackType,
    configIndex?: number,
  ): Promise<void> {
    if (value !== 0 && value !== 1 && value !== 2) {
      throw new RangeError(`Invalid vibration feedback type: ${value}`)
    }
    const idx = this.resolveConfigIndex(configIndex)
    const addr = getAddress(ADDRESS_LM20_RT.RT_VIBRATION_FEEDBACK_TYPE, idx)
    await sendWrite(this.device, addr, [value])
  }

  // ===== Beep（press / release / volume 各自直接读写 1 byte） =====

  async getBeepPress(side: RtSide, configIndex?: number): Promise<number> {
    return this.readRtField(side, 'beepPress', configIndex)
  }

  async setBeepPress(side: RtSide, value: number, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'beepPress', value, configIndex)
  }

  async getBeepRelease(side: RtSide, configIndex?: number): Promise<number> {
    return this.readRtField(side, 'beepRelease', configIndex)
  }

  async setBeepRelease(side: RtSide, value: number, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'beepRelease', value, configIndex)
  }

  async getBeepVolume(side: RtSide, configIndex?: number): Promise<number> {
    return this.readRtField(side, 'beepVolume', configIndex)
  }

  async setBeepVolume(side: RtSide, value: number, configIndex?: number): Promise<void> {
    await this.writeRtField(side, 'beepVolume', value, configIndex)
  }
}
